/**
 * Application Constants
 * 
 * Centralized constants for routes, storage keys, and API configurations
 * Migrated from the legacy src/utils/Constants.js
 */

export const ROUTES = {
  SIGN_IN: "/sign-in-sso",
  DASHBOARD: "/dashboard",
  ENVIRONMENT: "/environment",
  ADMIN: "/admin",
  REQUESTS: "/admin/requests",
  PROJECTS: "/admin/projects",
  USERS: "/admin/users",
  ROLES: "/admin/roles",
  AGENTS: "/admin/agents",
  INPUTS: "/inputs",
  LOAD: "/inputs/load",
  LOAD_DETAILS: "/inputs/load/details",
  TEST_LOGS_LOAD_DETAILS: "/test-logs/load/details",
  SCHEDULED_TEST_PLANS: "/scheduled-test-plans",
  SCHEDULED_TEST_RESULTS: "/scheduled-test-plans/results",
  SCHEDULED_TEST_RESULTS_OPEN: "/scheduled-test-plans/results/open",
  TEST_LOGS: "/test-logs",
  HELP: "/help",
  AI_GENERATE: "/ai-generate",
  ORGANIZATION_SIGNUP: "/organization-signup",
  ACCEPT_INVITE: "/accept-invite",
  ORGANIZATION_ADMIN: "/organization-admin",
  MOCK_SERVERS: "/mock-servers",
  SIGN_UP: "/sign-up",
  // Workflow
  WORKFLOW: "/workflow",
  WORKFLOW_DETAIL: "/workflow/:workflowId",
  // Role workspaces
  // Documents
  DOCUMENTS: "/documents",
  DOCUMENT_DETAIL: "/documents/:documentId",
  // Mock APIs & Test Cases
  MOCK_APIS: "/mock-apis",
  TEST_CASES: "/test-cases",
  // Integrations
  INTEGRATIONS: "/integrations",
};

export const PARTICIPANT_TYPES = {
  BA: { key: 'BA', label: 'Business Analyst', icon: 'HiDocumentText', color: 'blue', description: 'Create PRDs, BRDs, and requirement documents' },
  DB_ENGINEER: { key: 'DB_ENGINEER', label: 'Database Engineer', icon: 'HiDatabase', color: 'green', description: 'Design and review database schemas' },
  DESIGNER: { key: 'DESIGNER', label: 'Designer', icon: 'HiColorSwatch', color: 'purple', description: 'Create user stories and design screens' },
  FRONTEND_DEV: { key: 'FRONTEND_DEV', label: 'Frontend Developer', icon: 'HiCode', color: 'orange', description: 'Build UI with mock APIs' },
  BACKEND_ENGINEER: { key: 'BACKEND_ENGINEER', label: 'Backend Engineer', icon: 'HiServer', color: 'red', description: 'Implement APIs from contracts' },
  TESTER: { key: 'TESTER', label: 'Tester', icon: 'HiClipboardCheck', color: 'yellow', description: 'Execute test cases and report bugs' },
};

export const WORKFLOW_STAGES = {
  PRD: { key: 'PRD', label: 'Product Requirements', role: 'BA' },
  DB_DESIGN: { key: 'DB_DESIGN', label: 'Database Design', role: 'DB_ENGINEER' },
  DESIGN: { key: 'DESIGN', label: 'UI/UX Design', role: 'DESIGNER' },
  API_CONTRACT: { key: 'API_CONTRACT', label: 'API Contracts', role: 'FRONTEND_DEV' },
  MOCK_API: { key: 'MOCK_API', label: 'Mock APIs', role: 'FRONTEND_DEV' },
  IMPLEMENTATION: { key: 'IMPLEMENTATION', label: 'Implementation', role: 'BACKEND_ENGINEER' },
  TESTING: { key: 'TESTING', label: 'Testing', role: 'TESTER' },
};

export const STAGE_STATUSES = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  REVIEW: 'REVIEW',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  COMPLETE: 'COMPLETE',
  // A stage the team set aside; auto-advance hops over it and it can be
  // restored to PENDING. Only an untouched (PENDING) stage can be skipped.
  SKIPPED: 'SKIPPED',
};

export const DOCUMENT_TYPES = {
  PRD: 'PRD',
  BRD: 'BRD',
  USER_STORIES: 'USER_STORIES',
  SCHEMA: 'SCHEMA',
  API_CONTRACT: 'API_CONTRACT',
  TEST_CASES: 'TEST_CASES',
  CUSTOM: 'CUSTOM',
};

export const STORAGE_KEYS = {
  TOKEN: "token",
  METADATA: "metaData",
  PROJECTS: "projects",
  ROLE: "role",
  USERID: "userId",
  USERNAME: "userName",
  LAST_SELECTED_API: "lastSelectedApi",
  ORCHESTRATIONS: "orchestrations",
  ORGANIZATION_ID: "organizationId",
  ORGANIZATION_NAME: "organizationName",
  ORGANIZATION_ROLE: "organizationRole",
  INVITE_STATUS: "inviteStatus",
  ACTIVE_PARTICIPANT_ROLE: "activeParticipantRole",
};

export const RESPONSE_STATUS_CODE = {
  UPDATE_CREATE: "USER_CREATION",
  DELETE: "DELETE",
};

// Paths are root-relative on purpose: useAuthenticateFetch passes them straight
// to fetch() with no base prefix, so a leading slash is what routes them through
// the dev server's /api proxy. Without it the request resolves against the
// current route (e.g. /dashboard/api/projectusers/...) and 404s.
export const API_CALLS = {
  ProjectUser: {
    GetUser: "/api/projectusers/getAllUsers",
    AddUpdateUser: "/api/projectusers",
    DeleteUser: "/api/projectusers/deleteUser",
  },
};
